import { darkColor, mutedBlueColor, textColor } from "@/constants/colors";
import { css } from "@emotion/react";
import Container from "../Container/Container";
import {
  Body,
  BodyRegular14Css,
  H3Css,
  H5Css,
  H7Css,
  Heading,
} from "../Typography";
import Image from "next/image";
import { mq } from "@/constants/breakpoints";
import Balancer from "react-wrap-balancer";

const Box = ({
  icon,
  headline,
  body,
  isHighlighted = false,
}: {
  icon: any;
  headline: string;
  body: string;
  isHighlighted?: boolean;
}) => {
  return (
    <div
      css={css`
        padding: 30px 24px 24px;
        border-radius: 12px;
        background: ${isHighlighted
          ? "linear-gradient(269.36deg, #FCAF86 -25.75%, #CB8BEF 25.29%, #9294F6 67.97%, #5FD6B9 118.07%)"
          : "white"};
        ${mq["lg"]} {
          padding: 40px 44px 44px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          height: 216px;
          gap: 19px;
        }
      `}
    >
      <Heading
        level={3}
        css={css`
          color: ${isHighlighted ? "white" : darkColor};
          text-align: left;
          display: none;
          ${H5Css}
          ${mq["lg"]} {
            display: block;
          }
        `}
      >
        {headline}
      </Heading>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          ${mq["lg"]} {
            flex-direction: row;
            gap: 40px;
            align-items: center;
          }
        `}
      >
        <Image
          src={icon}
          alt={headline}
          css={css`
            margin: auto;
          `}
        />
        <Heading
          level={4}
          css={css`
            margin-top: 30px;
            color: ${isHighlighted ? "white" : darkColor};
            text-align: center;
            ${H7Css}
            ${mq["lg"]} {
              display: none;
            }
          `}
        >
          {headline}
        </Heading>
        <Body
          css={css`
            color: ${isHighlighted ? "white" : textColor};
            margin-top: 12px;
            text-align: center;
            ${BodyRegular14Css};
            ${mq["lg"]} {
              text-align: left;
            }
          `}
        >
          {body}
        </Body>
      </div>
    </div>
  );
};

interface MoreFeaturesSectionProps {
  isLastBoxHighlighted?: boolean;
  summaryItemsCollection: { items: any[] };
  summaryHeading: string;
  tlIcon: any;
  trIcon: any;
  blIcon: any;
  brIcon: any;
}

const MoreFeaturesSectionContentful = ({
  isLastBoxHighlighted = false,
  summaryItemsCollection,
  summaryHeading,
  tlIcon,
  trIcon,
  blIcon,
  brIcon,
}: MoreFeaturesSectionProps) => {
  const { items } = summaryItemsCollection;
  return (
    <div
      css={css`
        padding: 60px 0;
        background-color: ${mutedBlueColor};
        ${mq["lg"]} {
          padding: 100px 0;
        }
      `}
    >
      <Container>
        <Heading
          level={2}
          css={css`
            color: ${darkColor};
            text-align: center;
            ${H5Css}
            ${mq["lg"]} {
              ${H3Css}
            }
          `}
        >
          <Balancer>{summaryHeading}</Balancer>
        </Heading>
        <div
          css={css`
            margin-top: 24px;
            gap: 16px;
            display: grid;
            grid-template-columns: 1fr;
            ${mq["sm"]} {
              margin-top: 80px;
              grid-template-columns: 1fr 1fr;
              gap: 40px;
            }
          `}
        >
          <Box
            icon={tlIcon}
            headline={items[0].heading}
            body={items[0].description}
          />
          <Box
            icon={trIcon}
            headline={items[1].heading}
            body={items[1].description}
          />
          <Box
            icon={blIcon}
            headline={items[2].heading}
            body={items[2].description}
          />
          <Box
            icon={brIcon}
            headline={items[3].heading}
            body={items[3].description}
            isHighlighted={isLastBoxHighlighted}
          />
        </div>
      </Container>
    </div>
  );
};

export default MoreFeaturesSectionContentful;
